import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  importiereBuchungen,
  importZuruecksetzen,
  selectImportStatus,
} from 'modules/finanzen/features/finanzenSlice';
import { selectInvestitionen } from 'modules/investitionen/features/investitionenSlice';
import { fetchKontoweckerApi, markiereKontoweckerGelesenApi } from 'modules/finanzen/features/finanzenApi';
import { parseKontoweckerMails } from 'modules/finanzen/features/kontoweckerParser';
import { letzterOrt, merkeOrt } from 'modules/finanzen/features/ort';
import OrtAuswahl from 'modules/finanzen/components/OrtAuswahl';
import BuchungPruefliste from 'modules/finanzen/components/BuchungPruefliste';

/**
 * Verarbeitet ungelesene Sparkasse-Kontowecker-Mails: holt sie beim Oeffnen vom
 * Server (nur Roh-Mails), parst sie im Browser zu Buchungen, laesst sie
 * kontrollieren und verschluesselt speichern. Danach werden die Mails im Postfach
 * als gelesen markiert. Nur im entsperrten Modus nutzbar.
 */
export default function KontoweckerDialog({ onClose }) {
  const dispatch = useDispatch();
  const orte = useSelector(selectInvestitionen);
  const importStatus = useSelector(selectImportStatus);
  const [phase, setPhase] = useState('laden');
  const [fehler, setFehler] = useState(null);
  const [zeilen, setZeilen] = useState([]);
  const [markiert, setMarkiert] = useState(null);
  const [ort, setOrt] = useState(letzterOrt() || '');

  useEffect(() => {
    holeMails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const schliessen = () => {
    dispatch(importZuruecksetzen());
    onClose();
  };

  const holeMails = async () => {
    setPhase('laden');
    setFehler(null);
    try {
      const geparst = parseKontoweckerMails(await fetchKontoweckerApi());
      setZeilen(geparst);
      setMarkiert(geparst[0]?.schluessel || null);
      setPhase('pruefen');
    } catch (ex) {
      setFehler(fehlermeldung(ex));
      setPhase('fehler');
    }
  };

  const setzeKategorie = (schluessel, kategorie) =>
    setZeilen((prev) => prev.map((z) => (z.schluessel === schluessel ? { ...z, kategorie } : z)));
  const setzeAktiv = (schluessel, aktiv) =>
    setZeilen((prev) => prev.map((z) => (z.schluessel === schluessel ? { ...z, aktiv } : z)));
  const alleSetzen = (aktiv) => setZeilen((prev) => prev.map((z) => ({ ...z, aktiv })));

  const importieren = async () => {
    const auswahl = zeilen.filter((z) => z.aktiv);
    if (auswahl.length === 0) return;
    if (orte.length > 0 && !ort) {
      setFehler('Bitte einen Investitionsort für die Buchungen wählen.');
      return;
    }
    setFehler(null);
    const mitOrt = auswahl.map((z) => ({ ...z, ort: ort || null }));
    try {
      await dispatch(importiereBuchungen({ zeilen: mitOrt })).unwrap();
      merkeOrt(ort);
      const uids = auswahl.map((z) => z.uid).filter((u) => u != null);
      if (uids.length > 0) await markiereKontoweckerGelesenApi(uids);
      setPhase('fertig');
    } catch (ex) {
      setFehler('Import fehlgeschlagen. Bitte erneut versuchen.');
    }
  };

  const ortName = (orte.find((o) => o.uuid === ort) || {}).institut;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl bg-paper shadow-xl">
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h2 className="font-serif text-xl font-semibold text-ink">Kontowecker-Mails verarbeiten</h2>
          <button
            type="button"
            onClick={schliessen}
            className="rounded-lg px-2 py-1 text-ink-muted transition-colors hover:bg-ink/5"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          {importStatus.laeuft ? (
            <Fortschritt status={importStatus} />
          ) : phase === 'fertig' ? (
            <Ergebnis status={importStatus} onClose={schliessen} />
          ) : phase === 'laden' ? (
            <Zentriert>Rufe ungelesene Kontowecker-Mails ab…</Zentriert>
          ) : phase === 'fehler' ? (
            <Fehleransicht text={fehler} onNochmal={holeMails} onClose={schliessen} />
          ) : zeilen.length === 0 ? (
            <Zentriert>Keine ungelesenen Kontowecker-Mails gefunden.</Zentriert>
          ) : (
            <div className="flex h-full min-h-0 flex-col">
              {orte.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 border-b border-line px-6 py-3">
                  <span className="text-sm font-medium text-ink">Investitionsort</span>
                  <div className="min-w-[16rem]">
                    <OrtAuswahl wert={ort} onChange={setOrt} />
                  </div>
                </div>
              )}
              <div className="min-h-0 flex-1">
                <BuchungPruefliste
                  zeilen={zeilen}
                  zielName={ortName}
                  markiert={markiert}
                  onMarkieren={setMarkiert}
                  onKategorie={setzeKategorie}
                  onAktiv={setzeAktiv}
                  onAlle={alleSetzen}
                  onImport={importieren}
                  importLabel="übernehmen"
                />
              </div>
            </div>
          )}
        </div>

        {fehler && phase === 'pruefen' && !importStatus.laeuft && (
          <p className="border-t border-line px-6 py-2 text-sm text-red-700">{fehler}</p>
        )}
      </div>
    </div>
  );
}

/** Wandelt einen Abruf-Fehler in eine verstaendliche Meldung. */
function fehlermeldung(ex) {
  const status = ex?.response?.status;
  if (status === 503) return 'Der E-Mail-Abruf ist auf dem Server nicht konfiguriert (IMAP-Zugang fehlt).';
  return ex?.response?.data?.error || 'Mails konnten nicht abgerufen werden.';
}

/** Zentrierte Hinweiszeile. */
function Zentriert({ children }) {
  return <div className="flex h-full items-center justify-center p-10 text-center text-ink-muted">{children}</div>;
}

/** Fehleransicht mit Wiederholen/Schliessen. */
function Fehleransicht({ text, onNochmal, onClose }) {
  return (
    <div className="space-y-4 p-10 text-center">
      <p className="text-3xl">📭</p>
      <p className="text-ink">{text}</p>
      <div className="flex justify-center gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5"
        >
          Schließen
        </button>
        <button
          type="button"
          onClick={onNochmal}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500"
        >
          Erneut versuchen
        </button>
      </div>
    </div>
  );
}

/** Fortschrittsanzeige waehrend des Speicherns. */
function Fortschritt({ status }) {
  const prozent = status.gesamt > 0 ? Math.round((status.fertig / status.gesamt) * 100) : 0;
  return (
    <div className="space-y-4 p-10 text-center">
      <p className="text-ink">Verschlüssle und speichere… {status.fertig} von {status.gesamt}</p>
      <div className="mx-auto h-2 w-full max-w-md overflow-hidden rounded-full bg-ink/10">
        <div className="h-full bg-brand-600 transition-all" style={{ width: `${prozent}%` }} />
      </div>
    </div>
  );
}

/** Abschluss-Zusammenfassung nach dem Import. */
function Ergebnis({ status, onClose }) {
  return (
    <div className="space-y-4 p-10 text-center">
      <p className="text-4xl">✅</p>
      <p className="text-ink">
        {status.gesamt} Buchung(en) übernommen
        {status.uebersprungen > 0 && ` · ${status.uebersprungen} bereits vorhanden übersprungen`}.
      </p>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500"
      >
        Fertig
      </button>
    </div>
  );
}
